'use client'

/**
 * Inline loading Losi (next to tool / waiting labels):
 * - pops in from `logo` or `comet`
 * - tool markers → specialized `agent*` hold
 * - otherwise cycles connect / drift / comet
 * - face stays `neutre`
 * - every click → `jiggle` + five-tap Games ritual
 */

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'

import { LosiAvatar } from '@/components/losi-bot/losi-avatar'
import { useLosiGamesOptional } from '@/components/losi-games/losi-games-provider'
import {
  AMBIENT_LOADER_STATES,
  isToolHoldPose,
  type ChatLosiPose
} from '@/lib/losi-avatar/chat-status'
import { LOGO_INTRO } from '@/lib/losi-engine/losi'
import { STATE_BY_ID, type StateId } from '@/lib/losi-engine/states'
import { LOSI_JIGGLE_MS, losiMarkTapOrigin } from '@/lib/losi-games/mark-tap'
import { cn } from '@/lib/utils'

const COMET_INTRO_MS = 2600
const LOGO_INTRO_MS = Math.round(LOGO_INTRO * 1000) + 400
const AMBIENT_PAD_MS = 450

const INTROS = ['logo', 'comet'] as const
type IntroId = (typeof INTROS)[number]

function pickIntro(): IntroId {
  return INTROS[Math.floor(Math.random() * INTROS.length)]!
}

function holdMsFor(state: StateId): number {
  const def = STATE_BY_ID.get(state)
  const seconds = Math.max(def?.minDuration ?? 0, def?.duration ?? 2.2)
  return Math.round(seconds * 1000) + AMBIENT_PAD_MS
}

function nextAmbient(current: StateId, avoid?: StateId | null): StateId {
  const pool = AMBIENT_LOADER_STATES.filter((id) => id !== current && id !== avoid)
  const pickFrom = pool.length > 0 ? pool : [...AMBIENT_LOADER_STATES]
  return pickFrom[Math.floor(Math.random() * pickFrom.length)]!
}

export interface LoadingLosiMarkProps {
  size?: number
  className?: string
  pose?: ChatLosiPose
  shape?: string
  hoverState?: StateId | null
}

const DEFAULT_POSE: ChatLosiPose = {
  state: 'drift',
  status: 'streaming',
  drive: 'ambient'
}

export function LoadingLosiMark({
  size = 45,
  className,
  pose = DEFAULT_POSE,
  shape,
  hoverState = null
}: LoadingLosiMarkProps) {
  const introRef = useRef<IntroId>(pickIntro())
  const [introDone, setIntroDone] = useState(false)
  const [ambient, setAmbient] = useState<StateId>(() => nextAmbient(introRef.current))
  const [jiggling, setJiggling] = useState(false)
  const jiggleTimer = useRef(0)

  const toolHold = isToolHoldPose(pose)

  useEffect(() => {
    if (toolHold) {
      setIntroDone(true)
      return
    }

    const hold = introRef.current === 'comet' ? COMET_INTRO_MS : LOGO_INTRO_MS
    const timer = window.setTimeout(() => setIntroDone(true), hold)
    return () => window.clearTimeout(timer)
  }, [toolHold])

  useEffect(() => {
    if (!introDone || jiggling || toolHold) return

    const timer = window.setTimeout(() => {
      setAmbient((current) => nextAmbient(current, introRef.current))
    }, holdMsFor(ambient))

    return () => window.clearTimeout(timer)
  }, [introDone, jiggling, toolHold, ambient])

  const wasToolHold = useRef(toolHold)
  useEffect(() => {
    if (wasToolHold.current && !toolHold && introDone) {
      setAmbient((current) => nextAmbient(current))
    }
    wasToolHold.current = toolHold
  }, [toolHold, introDone])

  useEffect(() => () => window.clearTimeout(jiggleTimer.current), [])

  const state: StateId = useMemo(() => {
    if (jiggling) return 'jiggle'
    if (!introDone) return introRef.current
    if (toolHold) return pose.state
    return ambient
  }, [introDone, jiggling, toolHold, pose.state, ambient])

  const games = useLosiGamesOptional()

  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      games?.registerLosiTap(losiMarkTapOrigin(e))
      window.clearTimeout(jiggleTimer.current)
      setJiggling(true)
      jiggleTimer.current = window.setTimeout(() => setJiggling(false), LOSI_JIGGLE_MS)
    },
    [games]
  )

  return (
    <LosiAvatar
      state={state}
      expression="neutre"
      size={size}
      shape={shape}
      fitBody
      hoverState={jiggling ? null : hoverState}
      className={cn(className)}
      onClick={onClick}
      title="Click to jiggle"
    />
  )
}
